import React, { FC, useContext, useEffect, useState, useRef, useCallback, useMemo } from "react";
import TagCell from "./TagCell";
import { useNavigate } from "react-router";
import { AppContext } from "Context/AppContext";
import { Link } from "react-router";
import debounce from "lodash/debounce";
import { ADD_NEW, CANCEL, ITEMS, LISTS, LISTS_ROUTE, LIST_CREATE, LIST_DELETE, LIST_NAME, LIST_READ, LIST_UPDATE, ListsEditHeaders, SAVE } from "./constants";
import { ImageIcon as LegacyImageIcon } from "assests/GmsIcons";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import ProductAttributeImport from "../ISR/Pages/DefineModel/Components/ProductAttributeImport";
import useProductAttributeImport from "../ISR/Pages/DefineModel/Hooks/useProductAttributeImport";
import { toast } from "Components/DesignSystem/Toast/useToast";
import useTable from "Components/Common/Table/useTable";
import Typography from "Components/DesignSystem/Typography/Typography";
import Divider from "Components/DesignSystem/Divider/Divider";
import TextField from "Components/DesignSystem/TextField/TextField";
import Tag from "Components/DesignSystem/Tag/Tag";
import FieldTitle from "Components/DesignSystem/FieldTitle/FieldTitle";
import Card from "Components/DesignSystem/Card/Card";
import IconButton from "Components/DesignSystem/IconButton/IconButton";
import Toggle from "Components/DesignSystem/Toggle/Toggle";
import Tooltip from "Components/DesignSystem/Tooltip/Tooltip";
import Button from "Components/DesignSystem/Button/Button";
import PageSkeleton from "Components/DesignSystem/PageSkeleton/PageSkeleton";
import { Sort, ArrowUp, ArrowDown, InfoCircle, ChevronLeft, Add, Delete, Drag_n_drop, ImageIcon } from "Components/DesignSystem/Iconography/Icons";
import HelpTextDialog from "./HelpTextDialog";
import { HelpTextData } from "../ISR/Pages/DefineModel/model";
import { Actions } from "./Actions";
import { exportList, getListItem, saveList, updateList } from "./Services/ListServices";
import AuthManager from "Common/AuthManager";
import ValidateModal from "./ValidateModal";

interface Row {
  title: string;
  display_value: string;
  visibility: boolean;
  image: string;
  id?: string;
  math_value: number | null;
  tag?: string;
  helpText?: HelpTextData;
}

interface ListObj {
  id?: string;
  name: string;
  industry_id: string;
  items: Row[];
  imported_attribute: string;
  imported_attribute_name?: string;
  created_at: string;
  created_by: string;
  created_by_userid?: string;
  updated_at?: string;
  updated_by?: string;
}

const ListsEdit: FC = (props: any) => {
  let appContext = useContext(AppContext);
  let industryId = appContext && appContext?.get_selected_industry() && appContext?.get_selected_industry()?.id;
  const { params } = props.match;
  const { editId } = params;
  const [loading, setLoading] = useState(false);
  const appData = useContext(AppContext);
  const navigate = useNavigate();
  const [tableData, setTableData] = useState<Row[]>();
  const [models, setModels] = useState<string[]>([]);
  const [listName, setListName] = useState<string>();
  const [editState, setEditState] = useState<ListObj>();
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const { showAttributeMenu, setShowAttributeMenu, attributeData, selectedAttribute, setSelectedAttribute, selectedItems, setSelectedItems, fieldDataConfiguration, setFieldDataConfiguration, filteredAttributeData, openAttributeMenu, } =
    useProductAttributeImport({ attributeId: editState?.imported_attribute as string });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const { headers, sort, handleSortByColumn } =
    useTable({ table_headers: ListsEditHeaders });

  useEffect(() => {
    const { column, direction } = sort;
    if (!column || !direction) return;
    setTableData(prev => {
      if (!prev) return prev;
      return [...prev].sort((a, b) => {
        const valA = (a as any)[column] ?? "";
        const valB = (b as any)[column] ?? "";
        const numA = Number(valA);
        const numB = Number(valB);
        if (!isNaN(numA) && !isNaN(numB) && valA !== "" && valB !== "") {
          return direction === "asc" ? numA - numB : numB - numA;
        }
        const strA = valA.toString().toLowerCase();
        const strB = valB.toString().toLowerCase();
        if (strA < strB) return direction === "asc" ? -1 : 1;
        if (strA > strB) return direction === "asc" ? 1 : -1;
        return 0;
      });
    });
  }, [sort]);

  const [showImagePopup, setShowImagePopup] = useState(false)
  const [imageIndex, setImageIndex] = useState<number>()
  const [tempImage, setTempImage] = useState<string>("")

  const [lastTableRowRef, setLastTableRowRef] = useState<any>();
  const [showImportModal, setShowImportModal] = useState<boolean>(false);
  const [showValidateModal, setShowValidateModal] = useState<boolean>(false);
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>(undefined);
  const createPermission = appContext.pm.checkPermission(LIST_CREATE);
  const readPermission = appContext.pm.checkPermission(LIST_READ);
  const updatePermission = appContext.pm.checkPermission(LIST_UPDATE);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const skipNextAutoSaveRef = useRef(false);

  const performAutoSave = useCallback(
    debounce(async (name: string, items: Row[], stateId: string, industryIdVal: string, attrId: string, attrName: string) => {
      if (!stateId || !name || !updatePermission) return;
      setSaveStatus('saving');
      try {
        const requestBody = {
          id: stateId,
          name,
          items,
          industry_id: industryIdVal,
          imported_attribute: attrId,
          imported_attribute_name: attrName,
        };
        const res = await updateList(requestBody, stateId);
        if (res?.data?.done === false) {
          setSaveStatus('idle');
          toast.error(res?.data?.message ?? 'Failed to save list');
        } else {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 2000);
        }
      } catch (error) {
        setSaveStatus('idle');
        toast.error('Failed to save list');
        console.log(error);
      }
    }, 800),
    [updatePermission]
  );

  async function doExportList() {
    try {
      if (!editState?.id) return
      const res = await exportList(editState?.id)
      if (res?.done) {
        setDownloadUrl(res?.data?.url);
        toast.success("Downloaded successfully");
      }
      else throw new Error("Download Failed");
    }
    finally {
      setTimeout(() => {
        setDownloadUrl(undefined);
      }, 4000)
    }
  }

  const onOpenImportModal = () => setShowImportModal(true)
  const onCloseImportModal = () => setShowImportModal(false)
  const onOpenValidateModal = () => setShowValidateModal(true)
  const onCloseValidateModal = () => setShowValidateModal(false)
  const onExport = () => {
    doExportList();
  }
  const modalRoot = document.querySelector('#modal-root');

  useEffect(() => {
    if (lastTableRowRef) {
      lastTableRowRef?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
  }, [lastTableRowRef]);


  useEffect(() => {
    if (!readPermission) {
      navigate("/workspace/module/permission/denied");
    }
  }, [appData.channelKey]);

  useEffect(() => {
    const table: Row[] = fieldDataConfiguration.choices?.map((choice) => ({
      title: choice.av_value,
      display_value: choice.av_value,
      visibility: true,
      image: "",
      math_value: null
    })) || [];
    if (table.length === 0) return;
    setTableData((prev: Row[] | undefined) => {
      if (!prev) return table;
      const newTable = table.filter(x => !prev.find(y => y.title === x.title));
      if (newTable.length === 0) return prev;
      return [...prev, ...newTable];
    });
  }, [fieldDataConfiguration.choices]);

  useEffect(() => {
    if (![null, "null", "undefined", undefined].includes(editId)) { fetchEditState(); }
  }, []);

  useEffect(() => {
    if (editState?.id && listName && tableData) {
      if (skipNextAutoSaveRef.current) {
        skipNextAutoSaveRef.current = false;
        return;
      }
      performAutoSave(
        listName,
        tableData,
        editState.id,
        industryId as string,
        fieldDataConfiguration?.attributeId ?? '',
        fieldDataConfiguration?.attributeName ?? ''
      );
    }
  }, [listName, tableData]);

  const fetchEditState = async () => {
    try {
      setLoading(true);
      const res = await getListItem(editId);
      if (res?.data?.done) {
        const listData = res?.data?.data;
        const currentUserId = AuthManager.getAuthUser()?.profile?.id ?? '';
        const userIsAdmin = appContext.pm.checkIfUserIsAdmin() === true;
        const ownsItem = !listData?.created_by_userid || (currentUserId && String(listData?.created_by_userid) === String(currentUserId));
        if (!ownsItem && !userIsAdmin) {
          navigate(`${LISTS_ROUTE}/view/${editId}`);
          return;
        }
        skipNextAutoSaveRef.current = true;
        setListName(listData?.name);
        setTableData(listData?.items);
        setModels(listData?.models);
        setEditState(listData);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const items = Array.from(tableData ?? []);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setTableData(items);
  };

  const onInputChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
    type: string
  ) => {
    event.persist();
    const val = event?.target?.value;
    setTableData((prev) => {
      const newValue = prev?.map((entry, id) => {
        if (id === index) {
          switch (type) {
            case "title":
              entry.title = val;
              break;
            case "display_value":
              entry.display_value = val;
              break;
            case "math_value":
              if (isNaN(Number(val))) {
                toast.info("Math value must be an integer")
                break
              }
              entry.math_value = Number(val);
              break;
            default:
              break;
          }
        }
        return entry;
      });
      return newValue;
    });
  };

  const onVisibilityChangeHandler = (index: number) => {
    setTableData((prev) => {
      const newValue = prev?.map((entry, id) => {
        if (id === index) entry.visibility = !entry?.visibility;
        return entry;
      });
      return newValue;
    });
  };

  const onTagChangeHandler = (index: number, tag: string) => {
    setTableData((prev) =>
      prev?.map((entry, id) => id === index ? { ...entry, tag } : entry)
    );
  };

  const allTags = useMemo(
    () => Array.from(new Set((tableData ?? []).map(r => r.tag).filter((t): t is string => Boolean(t)))),
    [tableData]
  );

  const handleSave = async () => {
    try {
      if (createPermission) {
        if (listName) {
          setLoading(true)
          const requestBody = {
            name: listName,
            items: tableData,
            industry_id: industryId,
            imported_attribute: fieldDataConfiguration?.attributeId,
            imported_attribute_name: fieldDataConfiguration?.attributeName
          };
          const save = await saveList(requestBody);
          setLoading(false)
          if (save?.data.done === true) {
            toast.success(save?.data.message ?? "List Saved Successfully")
            if (save?.data?.data?.id) {
              navigate({
  pathname: `lists/edit/${save?.data?.data?.id}`
}, {
  replace: true
});
            }
            else {
              navigate("/gms/lists")
            }
          } else {
            toast.error(save?.data.message ?? "Failed to create list")
          }
        }
        else {
          toast.info("List name is mandatory")
        }
      }
      else toast.info("You don't have the permission to create list")
    } catch (error) {
      console.log(error);
    }
  };


  const convertToDataUrl = (file: File) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  async function handle_file_selection(event: any, index: number) {
    event.preventDefault();
    const file = event.target.files[0];
    setSelectedFile(file);
    const image = await convertToDataUrl(file);
    setTempImage(image as string);
  }

  const handleButtonClick = () => {
    fileInputRef?.current?.click();
  };

  const handleDelete = (e: any, index: number) => {
    if (appData?.pm?.checkPermission(LIST_DELETE)) {

      setTableData((prev) => {
        const newData = prev?.filter((x, ind) => ind !== index);
        return newData;
      });
    }
    else toast.info("You don't have the permission to delete")
  };

  const handleHelpText = (deleteRequested?: boolean, helpTextData?: HelpTextData) => {
    if (updatePermission) {
      setTableData((prev) => {
        const newData = prev?.map((x, ind) => {
          if (currentIndex === ind) {
            if (deleteRequested === true) {
              delete x.helpText;
              return x;
            }
            return { ...x, helpText: helpTextData };
          }
          return x
        });
        return newData;
      });
    }
    else toast.info("You don't have the permission to delete")
    setCurrentIndex(null);
  };

  const handleAdd = () => {
    setTableData((prev) => {
      const newData = [
        ...(prev || []),
        { title: "", display_value: "", visibility: true, image: "", math_value: null },
      ];
      return newData;
    });
  };

  const checkAttributeValue = (val: string) => {
    const values = fieldDataConfiguration?.choices?.map((x) => x?.value);
    return values?.includes(val);
  };

  return (
    <React.Fragment>
      <div className={`tw-px-[21px] tw-pt-[12px] tw-flex tw-items-center tw-gap-3`}>
        {/* DS ChevronLeft on role ink — LeftVectorIcon hardcoded stroke #172B4D,
            invisible on the dark ground (the MainPageHeader fix, same bug). */}
        <Link
          to={"/gms/lists"}
          aria-label="Back to lists"
          className="tw-flex tw-items-center tw-text-ink-secondary hover:tw-text-ink"
        >
          <ChevronLeft size={20} />
        </Link>
        {/* Was a <label>: the page had no h1 at all — § 81's header grammar.
            tw-text-ink is explicit: heading-2 is a size, not an ink, and the
            inherited default is black — invisible in dark. */}
        <Typography variant="display" as="h1" className="tw-text-ink" data-testid="list-edit-page-title">
          {LISTS}
        </Typography>
        {/* The autosave indicator — inline text, deliberately NOT a toast
            (okf/list/e2e.md § 5.2 pins the "Saving" → "Saved" copy). */}
        <div className={`tw-flex tw-items-center tw-ms-auto`} data-testid="list-edit-save-status">
          {saveStatus === 'saving' && (
            <span className="tw-text-ink-secondary tw-text-sm tw-mr-3">Saving</span>
          )}
          {saveStatus === 'saved' && (
            <span className="tw-text-tone-success-ink tw-text-sm tw-mr-3">Saved</span>
          )}
        </div>
      </div>
      <Divider className="tw-mt-2" data-testid="list-edit-header-divider" />
      {loading ? (
        <PageSkeleton
          preset="list"
          label="Loading list"
          data-testid="list-edit-skeleton"
          className="tw-px-[21px] tw-mt-3"
        />
      ) : (
        <>
          <div className="tw-flex tw-mt-2 tw-mb-2">
            <div className="tw-w-[30%] tw-ms-4">
              <TextField
                label={LIST_NAME}
                required
                placeholder="Enter Name"
                value={listName ?? ""}
                onChange={(value) => setListName(value)}
                data-testid="list-listedit-input-1"
                className="tw-max-w-[364px]"
              />
            </div>
            <div className="tw-w-[70%] tw-ml-16">
              <FieldTitle label="Associated Models" />
              <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-1" data-testid="list-listedit-models">
                {models?.length > 0 ? models?.map((model, index) =>
                  // Models are plain strings with no ids; index is the only stable key.
                  // eslint-disable-next-line react/no-array-index-key
                  <Tag key={index} label={model} variant="meta" data-testid={`list-listedit-model-${index}`} />
                ) : (
                  <Typography variant="body-primary" as="span" className="tw-text-ink-secondary">
                    No Models Associated
                  </Typography>
                )}
              </div>
            </div>
          </div>
          <div
            ref={(el) => {
              setLastTableRowRef(el)
            }}
            className="tw-ml-4 tw-w-[97%]"
          >
          <Card data-testid="list-listedit-items-panel">
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
              <Typography className="tw-text-ink" variant="title-header" as="h3">
                {ITEMS}
              </Typography>
              {!showAttributeMenu && <Actions
                onOpenImportModal={onOpenImportModal}
                onOpenValidateModal={onOpenValidateModal}
                onExport={onExport}
                disabled={false}
                openAttributeMenu={openAttributeMenu}
              />}
            </div>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="rows">
                {(provided) => (
                  <div className="tw-max-h-[510px] tw-overflow-x-auto ">
                    <table
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className=" tw-table-auto tw-w-full tw-overflow-scroll"
                    >
                      <thead className=" tw-sticky tw-top-0 tw-bg-surface-sunken  tw-border-edge tw-h-12 tw-font-noto-sans  tw-mt-[42px] tw-mb-[12px] tw-border tw-z-40">
                        <tr >
                          {headers.map(
                            (
                              {
                                label,
                                ...rest
                              }: {
                                label: string;
                                options: any;
                                map: string;
                              },
                              index: number
                            ) => {
                              return (
                                <th
                                  key={`${label}_${index}`}
                                  className="tw-sticky tw-text-left"
                                  scope="col"
                                  aria-sort={rest.options?.sort ? (sort.column === rest.map ? (sort.direction === "asc" ? "ascending" : "descending") : "none") : undefined}
                                >
                                  <div className="tw-flex tw-items-center tw-gap-1">
                                    {rest.options?.sort ? (
                                      /* A real button where a clickable div used to be —
                                         the testid predates the tag and stays verbatim. */
                                      <button
                                        type="button"
                                        data-testid={`list-listedit-div-1-${index}`}
                                        className="tw-flex tw-items-center tw-gap-1 tw-text-title-header tw-text-ink-secondary"
                                        onClick={() => handleSortByColumn(rest.map)}
                                      >
                                        <span>{label}</span>
                                        {sort.column === rest.map
                                          ? (sort.direction === "asc" ? <ArrowUp /> : <ArrowDown />)
                                          : <Sort />}
                                      </button>
                                    ) : (
                                      <span
                                        data-testid={`list-listedit-div-1-${index}`}
                                        className="tw-text-title-header tw-text-ink-secondary"
                                      >
                                        {label}
                                      </span>
                                    )}
                                    {rest.options?.infoMessage && (
                                      <Tooltip content={rest.options?.infoMessage} usePortal>
                                        <Button variant="subtle" size="sm" aria-label={`About ${label}`}>
                                          <InfoCircle size={16} />
                                        </Button>
                                      </Tooltip>
                                    )}
                                  </div>
                                </th>
                              );
                            }
                          )}
                        </tr>
                      </thead>
                      <tbody >
                        {tableData?.map((item: Row, index) => {
                          const hasHelpText = Object.prototype.hasOwnProperty.call(item, "helpText")
                          return (
                            <Draggable
                              key={index}
                              draggableId={`${index}`}
                              index={index}
                              isDragDisabled={false}
                            >
                              {(provided) => (
                                <tr
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`tw-w-full tw-h-12 tw-bg-surface  tw-border tw-border-edge`}
                                >
                                  <td
                                    className={`tw-h-12 ${index % 2 === 0
                                      ? "tw-bg-surface"
                                      : "tw-bg-surface-sunken"
                                      } tw-pl-4 tw-pr-4`}
                                  >
                                    <Drag_n_drop />
                                  </td>
                                  <td
                                    className={`tw-h-12 tw-w-10 tw-font-semibold tw-text-ink-secondary tw-select-none ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"}`}
                                  >
                                    {index + 1}
                                  </td>
                                  <td data-testid={`list-listedit-image-${item.id ?? index}`}
                                    className={`tw-h-12 tw-max-h-12 ${index % 2 === 0
                                      ? "tw-bg-surface"
                                      : "tw-bg-surface-sunken"
                                      } tw-cursor-pointer`}
                                    onClick={() => {
                                      setShowImagePopup(true);
                                      setSelectedFile(null);
                                      setImageIndex(index);
                                      setTempImage(tableData?.[index]?.image ?? "");
                                    }}
                                  >
                                    {item?.image ? (
                                      <img
                                        src={item?.image}
                                        alt="image"
                                        className="tw-h-12 tw-w-12"
                                      />
                                    ) : (
                                      <ImageIcon size={24} className="tw-text-ink-tertiary" />
                                    )}
                                  </td>
                                  <td
                                    className={`tw-h-12 tw-w-[160px] tw-min-w-[160px] tw-max-w-[160px] ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"}`}
                                  >
                                    <TagCell
                                      value={item.tag || ""}
                                      onChange={(tag) => onTagChangeHandler(index, tag)}
                                      allTags={allTags}
                                      data-testid={`list-tagcell-${item.id ?? index}`}
                                    />
                                  </td>
                                  <td
                                    className={`tw-h-12 ${index % 2 === 0
                                      ? "tw-bg-surface"
                                      : "tw-bg-surface-sunken"
                                      } tw-w-[30%]`}
                                  >
                                    <TextField
                                      label=""
                                      aria-label={`Title for row ${index + 1}`}
                                      placeholder="Enter Title"
                                      value={item.title ?? ""}
                                      /* Imported-attribute rows keep their Title locked (§ 5.2). */
                                      disabled={checkAttributeValue(item?.title)}
                                      onChange={(_value, e) => {
                                        if (e) onInputChangeHandler(e, index, "title");
                                      }}
                                      data-testid={`list-listedit-title-${item.id ?? index}`}
                                      className="tw-w-[95%]"
                                    />
                                  </td>
                                  <td
                                    className={`tw-h-12 ${index % 2 === 0
                                      ? "tw-bg-surface"
                                      : "tw-bg-surface-sunken"
                                      } tw-w-[30%]`}
                                  >
                                    <TextField
                                      label=""
                                      aria-label={`Display value for row ${index + 1}`}
                                      placeholder="Enter Display Value"
                                      value={item.display_value ?? ""}
                                      onChange={(_value, e) => {
                                        if (e) onInputChangeHandler(e, index, "display_value");
                                      }}
                                      data-testid={`list-listedit-display-value-${item.id ?? index}`}
                                      className="tw-w-[95%]"
                                    />
                                  </td>
                                  <td
                                    className={`tw-h-12 ${index % 2 === 0
                                      ? "tw-bg-surface"
                                      : "tw-bg-surface-sunken"
                                      } tw-w-[20]`}
                                  >
                                    <TextField
                                      label=""
                                      aria-label={`Math value for row ${index + 1}`}
                                      placeholder="Enter Math Value"
                                      type="number"
                                      value={item.math_value?.toString() ?? ""}
                                      onChange={(_value, e) => {
                                        if (e) onInputChangeHandler(e, index, "math_value");
                                      }}
                                      data-testid={`list-listedit-math-value-${item.id ?? index}`}
                                      className="tw-w-[95%]"
                                    />
                                  </td>
                                  <td
                                    className={`tw-h-12 ${index % 2 === 0
                                      ? "tw-bg-surface"
                                      : "tw-bg-surface-sunken"
                                      }`}
                                  >
                                    <Toggle
                                      label=""
                                      aria-label={`Show or hide ${item.title || `row ${index + 1}`}`}
                                      checked={!!item?.visibility}
                                      onChange={() =>
                                        onVisibilityChangeHandler(index)
                                      }
                                      data-testid={`list-listedit-visibility-${item.id ?? index}`}
                                    />
                                  </td>
                                  <td className={`tw-h-12 ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"}`}>
                                    {/* § 212 — glyph-only button with no accessible name; the
                                        square provides the hit area the p-2.5 wrapper faked.
                                        DS glyphs (currentColor) since the dark-mode re-ink. */}
                                    <IconButton data-testid={`list-listedit-help-text-${item.id ?? index}`}
                                      aria-label={hasHelpText ? "Edit help text" : "Add help text"}
                                      onClick={() => {
                                        setCurrentIndex(index)
                                      }}
                                    >
                                      {hasHelpText ?
                                        <InfoCircle size={16} /> :
                                        <Add />
                                      }
                                    </IconButton>
                                  </td>
                                  {!checkAttributeValue(item?.title) ? (
                                    <td
                                      className={`tw-h-12 ${index % 2 === 0
                                        ? "tw-bg-surface"
                                        : "tw-bg-surface-sunken"
                                        }`}
                                    >
                                      {/* § 212 danger-subtle — the row delete: quiet until
                                          hovered onto its danger tint. Same handler. */}
                                      <IconButton aria-label="Delete" data-testid={`list-listedit-delete-row-${item.id ?? index}`}
                                        variant="danger-subtle"
                                        onClick={(e) => {
                                          handleDelete(e, index);
                                        }}
                                      >
                                        <Delete />
                                      </IconButton>
                                    </td>
                                  ) : (
                                    <td
                                      className={`tw-h-12 ${index % 2 === 0
                                        ? "tw-bg-surface"
                                        : "tw-bg-surface-sunken"
                                        }`}
                                    ></td>
                                  )}
                                </tr>
                              )}
                            </Draggable>
                          );
                        })}

                        {provided.placeholder}
                      </tbody>
                    </table>
                  </div>

                )}
              </Droppable>
            </DragDropContext>
            <button
              data-testid="list-edit-add-new-btn"
              /* Link-styled add action — the § 81 link ink (accent-bold reads
                 as clickable where resting accent does not). */
              className="tw-text-accent-bold hover:tw-underline tw-mt-4"
              onClick={handleAdd}
            >
              {ADD_NEW}
            </button>
          </Card>
          </div>
        </>
      )}
      {showAttributeMenu && (
        <ProductAttributeImport
          attributeData={attributeData}
          showAttributeMenu={showAttributeMenu}
          setShowAttributeMenu={setShowAttributeMenu}
          filteredAttributeData={filteredAttributeData}
          selectedAttribute={selectedAttribute}
          setSelectedAttribute={setSelectedAttribute}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          fieldDataConfiguration={fieldDataConfiguration}
          setFieldDataConfiguration={setFieldDataConfiguration}
        />
      )}
      {showImagePopup && (
        <div className=" tw-inset-0 tw-bg-scrim tw-z-40 tw-fixed tw-top-0 tw-left-0 tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
          <div className="tw-w-[613px] tw-h-[280px] tw-bg-surface tw-rounded tw-shadow tw-border tw-border-edge">
            <div className=" tw-font-noto-sans tw-text-sm tw-font-bold tw-m-2">
              {imageIndex !== undefined && tableData?.[imageIndex]?.title
                ? `Title: ${tableData[imageIndex].title}`
                : 'Item Image'}
            </div>
            <div className="tw-w-[100%] tw-h-[0px] tw-border tw-border-edge"></div>
            <div>
              <div className="tw-flex tw-align-middle tw-m-2 tw-mt-4">
                <div className="tw-p-1 tw-ml-2 tw-mr-2 tw-border">
                  {tempImage ?
                    <img alt='attr-value-image' src={tempImage}
                      className="tw-w-[59px] tw-h-[59px] tw-align-middle tw-justify-center"
                    />
                    :
                    <LegacyImageIcon width={59} height={59} />
                  }</div>
                <div className="tw-ml-2">
                  <div className="tw-m-1">
                    {selectedFile ? selectedFile?.name : tempImage ? "" : "No image available"}
                  </div>
                  <input data-testid="list-listedit-input-7"
                    type="file"
                    ref={fileInputRef}
                    className=" tw-hidden"
                    onChange={(event) => {
                      handle_file_selection(event, imageIndex as number);
                    }}
                  />
                  <button data-testid="list-listedit-button-8"
                    onClick={handleButtonClick}
                    className=" tw-h-8 tw-px-[22px] tw-py-2 tw-bg-surface tw-rounded tw-border tw-border-accent tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex"
                  >
                    <span className="tw-text-center tw-text-accent tw-text-sm tw-font-normal font-['Noto Sans'] leading-tight">
                      {"Browse File"}
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <div className=" tw-ml-4 tw-mt-6 tw-text-ink-secondary tw-text-xs tw-font-bold tw-font-['Noto Sans'] tw-leading-tight">
              Image URL
            </div>
            <input data-testid="list-listedit-input-9"
              placeholder="Enter Url"
              className="tw-p-2 tw-ml-4 tw-mr-4 tw-mt-3 tw-mb-2 tw-w-[95%] tw-h-9 tw-bg-surface tw-rounded tw-border tw-border-edge-strong tw-text-ink placeholder:tw-text-ink-secondary"
              onChange={(e) => {
                e.preventDefault()
                setTempImage(e?.target?.value);
              }}
              value={tempImage}
            ></input>
            <div className="tw-float-right">
              <button data-testid="list-listedit-button-10" onClick={() => {
                setShowImagePopup(false);
                setSelectedFile(null);
                setTempImage("");
              }} className="tw-m-2 tw-h-8 tw-px-[22px] tw-py-2 tw-bg-surface tw-rounded tw-border tw-border-accent tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
                <span className="tw-text-center tw-text-accent tw-text-sm tw-font-normal font-['Noto Sans'] leading-tight">
                  {CANCEL}
                </span>
              </button>
              <button data-testid="list-listedit-button-11" onClick={() => {
                setTableData((prev) => {
                  const newValue = prev?.map((entry, id) => {
                    if (id === imageIndex) entry.image = tempImage;
                    return entry;
                  });
                  return newValue;
                });
                setShowImagePopup(false);
                setImageIndex(undefined);
                setSelectedFile(null);
                setTempImage("");
              }} className=" tw-h-8 tw-m-2 tw-px-[22px] tw-py-2 tw-text-ink-inverse tw-rounded tw-border tw-bg-accent-bold tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
                <span className="tw-text-center tw-text-ink-inverse tw-text-sm tw-font-normal font-['Noto Sans'] leading-tight">
                  {SAVE}
                </span>
              </button>
            </div>

          </div>
        </div>

      )}
      {currentIndex != null ? <HelpTextDialog selectedItemId={editState?.id}
        savedHelpTextData={tableData != null ? tableData[currentIndex].helpText : null}
        onDelete={() => handleHelpText(true)}
        onClose={() => setCurrentIndex(null)}
        onSubmit={(helpTextData) => handleHelpText(false, helpTextData)} /> : null}
      {(showValidateModal || showImportModal) && <ValidateModal isOpen={showImportModal === true ? showImportModal : showValidateModal} onClose={showImportModal === true ? onCloseImportModal : onCloseValidateModal} listId={editState?.id} action={showImportModal ? "Import" : "Validate"} key={showImportModal ? 'import-modal' : 'validate-modal'} fetchEditState={fetchEditState} />}
      {modalRoot && <iframe style={{ display: "none" }} src={downloadUrl} id="iframe_download_list_template" title="Download Template" />}
    </React.Fragment>
  );
};


export default ListsEdit;

